/**
 * P1 · Sign-in / Sign-up — REAL BetterAuth email+password.
 *
 * ZERO-PII: no name, no profile — the e-mail is the only identifier stored
 * (credentials live in the org's own EU-hosted Postgres). Social login is the
 * primary path when providers are configured; profiles are stripped server-side.
 * A brand-new account lands on the create-organization gate inside /app.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInEmail, signInSocial, signUpEmail } from '../lib/authApi';
import { mergeProviders } from '../lib/providers';
import { useSession } from '../lib/session';

type Mode = 'signin' | 'signup';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

/** Social providers enabled at BUILD time (VITE_SOCIAL_PROVIDERS="google,microsoft,github");
 * unioned at runtime with GET /api/auth-providers (public). Zero-PII: the backend strips
 * profile name/avatar before persisting (mapProfileToUser). */
const BUILD_PROVIDERS: string[] = ((import.meta.env.VITE_SOCIAL_PROVIDERS as string | undefined) ?? '')
  .split(',').map((s) => s.trim()).filter(Boolean);

function providerButton(id: string): { id: string; label: string; glyph: string } {
  return {
    id,
    label: id === 'google' ? 'Google' : id === 'microsoft' ? 'Microsoft' : id === 'github' ? 'GitHub' : id,
    glyph: id === 'google' ? '◉' : id === 'microsoft' ? '◆' : '❖',
  };
}

function Shield() {
  return (
    <svg width="26" height="30" viewBox="0 0 64 72" fill="none" aria-hidden="true">
      <path d="M 10,8 L 54,8 L 54,52 L 32,66 L 10,52 Z" fill="none" stroke="var(--teal)" strokeWidth="8" strokeLinejoin="miter" strokeLinecap="square" />
    </svg>
  );
}

export function SignIn() {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runtimeProviders, setRuntimeProviders] = useState<string[]>([]);

  // Runtime provider discovery — public endpoint, no auth. Silent failure keeps
  // the fixtures demo (no backend) rendering exactly the build-time list.
  useEffect(() => {
    let live = true;
    fetch(`${API_BASE}/api/auth-providers`)
      .then((r) => (r.ok ? (r.json() as Promise<{ providers?: unknown }>) : null))
      .then((body) => {
        if (!live || !body || !Array.isArray(body.providers)) return;
        setRuntimeProviders(body.providers.filter((p): p is string => typeof p === 'string'));
      })
      .catch(() => { /* no backend — build-time providers only */ });
    return () => { live = false; };
  }, []);

  const social = mergeProviders(BUILD_PROVIDERS, runtimeProviders).map(providerButton);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    const err =
      mode === 'signup'
        ? await signUpEmail(email.trim(), password)
        : await signInEmail(email.trim(), password);
    if (err) {
      setError(err);
      setBusy(false);
      return;
    }
    await refresh();
    navigate('/app/today');
  }

  const label = mode === 'signin' ? 'Sign in' : 'Create account';

  return (
    <div className="signin-wrap">
      <div className="card signin-card">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
          <Shield />
        </div>
        <h2>{mode === 'signin' ? 'Sign in to BicPort' : 'Create your account'}</h2>
        <div className="sub">
          {mode === 'signin'
            ? 'Access your CRA Article 14 console.'
            : 'Zero PII — no name, no profile. Your e-mail is the login identifier, nothing more.'}
        </div>

        {social.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {social.map((p) => (
              <button key={p.id} type="button" className="idp" data-testid={`social-${p.id}`} disabled={busy}
                onClick={() => { setError(null); void signInSocial(p.id).then(({ url, error: e }) => { if (url) window.location.assign(url); else setError(e); }); }}>
                <span aria-hidden="true">{p.glyph}</span> Continue with {p.label}
              </button>
            ))}
            <div className="hint" style={{ textAlign: 'center' }}>— or use e-mail —</div>
          </div>
        )}

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'left' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className="hint">Work e-mail</span>
            <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className="hint">Password</span>
            <input
              className="input" type="password" required minLength={8} value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            />
          </label>

          {error && (
            <div className="hint" style={{ color: 'var(--danger, #c0392b)', border: '1px solid color-mix(in srgb, var(--danger, #c0392b) 35%, var(--border))', borderRadius: 8, padding: '8px 10px' }}>
              {error}
            </div>
          )}

          <button className="btn btn-primary" type="submit" disabled={busy} style={{ marginTop: 6 }}>
            {busy ? '…' : `${label} →`}
          </button>
        </form>

        <div className="hint" style={{ marginTop: 18, textAlign: 'center' }}>
          {mode === 'signin' ? (
            <>
              No account yet?{' '}
              <a style={{ color: 'var(--teal)', cursor: 'pointer' }} onClick={() => { setMode('signup'); setError(null); }}>
                Create one
              </a>
            </>
          ) : (
            <>
              Already registered?{' '}
              <a style={{ color: 'var(--teal)', cursor: 'pointer' }} onClick={() => { setMode('signin'); setError(null); }}>
                Sign in
              </a>
            </>
          )}
        </div>

        <div className="nopii" style={{ alignItems: 'flex-start', textAlign: 'left' }}>
          <b>✓</b>
          <span>
            Zero PII by design: we never store names or profiles. Only your login e-mail and your
            org role live in your organisation's own EU-hosted database.
          </span>
        </div>
      </div>
    </div>
  );
}
